import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import { company } from '~/company/Company';
import {
  type NavigationVariant,
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '~/components/isolated/Carousel';
import ProductCardSkeletonFactory from '~/components/ProductCardV3/skeletons/ProductCardSkeletonFactory';
import ProductCardM from '~/components/ProductCardV3/variants/ProductCardM/ProductCardM';
import ProductCardXL from '~/components/ProductCardV3/variants/ProductCardXL/ProductCardXL';
import { useGlobal } from '~/hooks/useGlobal';
import { Category } from '~/stores/CategoriesStore';

import { createCategoryUrl } from '../shared/utils';

import styles from './ProductsCarouselSliderOneBlock.module.scss';

interface ProductsCarouselSliderOneBlockProps {
  category: Category;
  navigationVariant?: NavigationVariant;
}

const ProductsCarouselSliderOneBlock = ({
  category,
  navigationVariant = 'default',
}: ProductsCarouselSliderOneBlockProps) => {
  const { pathname } = useLocation();
  const { isMobile } = useGlobal();

  if (!category.products.length && category.productsRequest.isFulfilled) {
    return null;
  }

  const categoryId = category?.subcategory[0]?.id;

  if (!category.subcategory.length || !categoryId) {
    return null;
  }

  const linkTo =
    pathname !== '/search' ? createCategoryUrl({ id: categoryId }) : undefined;

  const products = category.products.slice(0, isMobile ? 10 : 15);
  const items = products.map((product) => ({
    id: product.id,
    node: company.isMarketplaceVariant ? (
      <ProductCardXL product={product} />
    ) : (
      <ProductCardM product={product} />
    ),
  }));

  const skeletonComponent = (
    <div className={styles.skeletonContainer}>
      {Array.from({ length: 5 }, (_, i) => (
        <ProductCardSkeletonFactory key={`skeleton-${i}`} variant="m" />
      ))}
    </div>
  );

  const commonProps = {
    title: category.name,
    linkTo,
    categoryId,
    items,
    skeletonComponent: items.length === 0 ? skeletonComponent : undefined,
  };

  return (
    <div className={styles.container}>
      {isMobile ? (
        <ProductCarouselMobile
          {...commonProps}
          itemClassName={styles.item}
          showViewAllOnMobile
        />
      ) : (
        <ProductCarouselDesktop
          {...commonProps}
          slideClassName={
            company.isMarketplaceVariant
              ? styles.slideMarketplace
              : styles.slide
          }
          contentClassName={styles.carouselContent}
          navigationVariant={navigationVariant}
        />
      )}
    </div>
  );
};

export default observer(ProductsCarouselSliderOneBlock);
